<template>
  <div class="ad-list-view">
    <b-overlay :show="loading" rounded opacity="0.6" spinner-small>
      <div class="ad-list-card">
        <div class="ad-list-page-heading">
          <div class="ad-list-page-heading__main">
            <h2 class="ad-list-page-title">{{ $t("lbl_EMPBE_AdInfoCenter") }}</h2>
            <span v-if="toBeSubmittedCount > 0" class="ad-list-pending-pill">
              {{ $t("lbl_EMPBE_advertisingInfo_ToBeSubmittedCount", { count: toBeSubmittedCount }) }}
            </span>
          </div>
          <div
            v-if="!fromAdmin && !showEmptyPlaceholder"
            ref="exploreMoreAdsWrap"
            class="ad-list-explore-wrap"
          >
            <button
              type="button"
              class="ad-list-explore-btn"
              :t_label="adTLabel('List', 'btnExploreMoreAds')"
              :aria-expanded="showExploreMoreAdsPanel ? 'true' : 'false'"
              @click="toggleExploreMoreAdsPanel"
            >
              <i class="fa fa-info-circle ad-list-explore-btn__icon" aria-hidden="true"></i>
              <span class="ad-list-explore-btn__text">{{ $t("lbl_EMPBE_FullAdvertisingOptions") }}</span>
            </button>
            <div
              v-show="showExploreMoreAdsPanel"
              class="ad-list-explore-panel"
              role="dialog"
              aria-label="Explore More Ads"
            >
              <MoreAdvertisingOptions
                variant="panel"
                :cdnURL="cdnURL"
                :langid="String(langid || '1252')"
                :learn-more-url="learnMoreUrl"
                @contact-sales="openContactSales"
              />
            </div>
          </div>
        </div>

        <div v-if="rejectedCount > 0" class="ad-list-reject-banner">
          <div class="ad-list-reject-banner__head">
            <img
              class="ad-list-reject-banner__icon"
              :src="cdnURL + 'svg/PublicTabs/warn.svg'"
              alt=""
            />
            <span class="ad-list-reject-banner__title">
              {{ $t("lbl_EMPBE_advertisingInfo_RejectedBannerTitle", { count: rejectedCount }) }}
            </span>
          </div>
          <p class="ad-list-reject-banner__desc">
            {{ $t("lbl_EMPBE_advertisingInfo_RejectedBannerDesc") }}
          </p>
        </div>

        <MoreAdvertisingOptions
          v-if="showEmptyPlaceholder"
          variant="page"
          :cdnURL="cdnURL"
          :langid="String(langid || '1252')"
          :learn-more-url="learnMoreUrl"
          @contact-sales="openContactSales"
        />

        <localization v-else :language="kendoLang">
          <intl :locale="kendoLang">
            <div class="ad-list-table-shell">
              <div class="ad-list-chrome">
                <div
                  class="ad-list-chrome-row ad-list-chrome-row--head"
                  :style="{ gridTemplateColumns: adListGridColumns }"
                >
                  <div
                    v-for="col in columns"
                    :key="`head-${col.field}`"
                    class="ad-list-chrome-cell"
                  >
                    {{ col.title }}
                  </div>
                </div>
                <div
                  class="ad-list-chrome-row ad-list-chrome-row--filter"
                  :style="{ gridTemplateColumns: adListGridColumns }"
                >
                  <div class="ad-list-chrome-cell">
                    <DropDownList
                      :t_label="adTLabel('List', 'filterDisplaySection')"
                      :data-items="displayInFilterOptions"
                      text-field="text"
                      data-item-key="value"
                      :value="platformFilterValue"
                      @change="(ev) => applySelectFilter('displaySection', ev.value.value)"
                    />
                  </div>
                  <div class="ad-list-chrome-cell">
                    <DropDownList
                      :t_label="adTLabel('List', 'filterPageType')"
                      :data-items="pageTypeFilterOptions"
                      text-field="text"
                      data-item-key="value"
                      :value="pageTypeFilterValue"
                      @change="(ev) => applySelectFilter('pageType', ev.value.value)"
                    />
                  </div>
                  <div class="ad-list-chrome-cell">
                    <k-input
                      :t_label="adTLabel('List', 'filterKeyword')"
                      :placeholder="$t('lbl_EMPBE_AdList_SearchByKeyword')"
                      :value="keywordFilter"
                      @input="applyKeywordFilter"
                    />
                  </div>
                  <div class="ad-list-chrome-cell">
                    <DropDownList
                      :t_label="adTLabel('List', 'filterLaunchStatus')"
                      :data-items="launchStatusFilterOptions"
                      text-field="text"
                      data-item-key="value"
                      :value="launchStatusFilterValue"
                      @change="(ev) => applySelectFilter('launchStatus', ev.value.value)"
                    />
                  </div>
                  <div class="ad-list-chrome-cell">
                    <DropDownList
                      :t_label="adTLabel('List', 'filterDisplayStatus')"
                      :data-items="statusFilterOptions"
                      text-field="text"
                      data-item-key="value"
                      :value="statusFilterValue"
                      @change="(ev) => applySelectFilter('displayStatus', ev.value.value)"
                    />
                  </div>
                  <div class="ad-list-chrome-cell">
                    <DropDownList
                      :t_label="adTLabel('List', 'filterEffectiveDateSort')"
                      :data-items="dateSortingData"
                      text-field="text"
                      data-item-key="value"
                      :value="effectiveDateSortValue"
                      @change="(ev) => applyDateSort('effectiveDateFrom', ev.value.value)"
                    />
                  </div>
                  <div class="ad-list-chrome-cell">
                    <DropDownList
                      :t_label="adTLabel('List', 'filterLastSubmitDateSort')"
                      :data-items="dateSortingData"
                      text-field="text"
                      data-item-key="value"
                      :value="lastSubmitDateSortValue"
                      @change="(ev) => applyDateSort('lastSubmitDate', ev.value.value)"
                    />
                  </div>
                </div>
              </div>
              <div class="ad-list-body">
                <div
                  v-for="row in items"
                  :key="row.adDetailId"
                  class="ad-list-data-row"
                  :class="{ 'ad-list-data-row--rejected': isRejectedRow(row) }"
                  :style="{ gridTemplateColumns: adListGridColumns }"
                  role="button"
                  tabindex="0"
                  :t_label="adTLabel('List', 'btnOpenRow', row.subType, row.adDetailId)"
                  @click="onDataRowClick(row, $event)"
                  @keydown.enter.prevent="goToDetail(row)"
                  @keydown.space.prevent="goToDetail(row)"
                >
                  <div class="ad-list-chrome-cell">
                    <div class="ad-list-platform-cell">
                      <span class="ad-list-platform-logo">
                        <img
                          :src="platformLogoUrl(row.displaySection)"
                          :alt="row.displayInLabel"
                          @error="onPlatformLogoError($event)"
                        />
                      </span>
                      <span class="ad-list-platform-name">{{ row.displayInLabel }}</span>
                    </div>
                  </div>
                  <div class="ad-list-chrome-cell">
                    <span class="ad-list-page-type">{{ row.pageTypeLabel }}</span>
                  </div>
                  <div class="ad-list-chrome-cell">
                    <div class="ad-list-item-cell">
                      <span class="ad-list-thumb-wrap">
                        <img
                          :src="adListThumbSrc(row)"
                          class="ad-list-thumb"
                          alt=""
                          @error="onAdListThumbError($event, row)"
                        />
                      </span>
                      <div class="ad-list-item-text">
                        <div
                          class="ad-list-item-title"
                          :class="{ 'ad-list-item-title--rejected': isRejectedRow(row) }"
                        >
                          {{ row.adItemLabel }}
                        </div>
                        <div class="ad-list-item-id">
                          <template v-if="row.showPairedDetailIds">
                            <span>{{ $t("lbl_EMPBE_PC") }}: #{{ row.pcAdDetailId }}</span>
                            <span class="ad-list-item-id-sep">|</span>
                            <span>{{ $t("lbl_EMPBE_Mobile") }}: #{{ row.mobileAdDetailId }}</span>
                          </template>
                          <template v-else>#{{ row.adDetailId }}</template>
                        </div>
                      </div>
                      <span
                        v-if="hasRowDraftIndicator(row)"
                        class="ad-list-draft-pill"
                        role="status"
                      >
                        <svg class="ad-list-draft-pill__icon" width="14" height="16" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M4 11H12V9H4V11ZM4 14H12V12H4V14ZM4 17H9V15H4V17ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H10L16 6V18C16 18.55 15.8042 19.0208 15.4125 19.4125C15.0208 19.8042 14.55 20 14 20H2ZM9 7V2H2V18H14V7H9Z" fill="#5D5125"/>
                        </svg>
                        {{ $t("lbl_EMPBE_Draft") }}
                      </span>
                      <button
                        type="button"
                        class="ad-list-edit-btn"
                        @click.stop="goToDetail(row)"
                        :t_label="adTLabel('List', 'btnEdit', row.subType, row.adDetailId)"
                      >
                        <i class="fa fa-pencil ad-list-edit-icon"></i>
                        {{ $t("lbl_EMPBE_Edit") }}
                      </button>
                    </div>
                  </div>
                  <div class="ad-list-chrome-cell ad-list-chrome-cell--launch">
                    <span
                      class="ad-list-launch-badge"
                      :class="isRowLaunchOnline(row) ? 'is-online' : 'is-offline'"
                    >
                      <i class="ad-list-launch-badge__dot" aria-hidden="true"></i>
                      {{ isRowLaunchOnline(row) ? $t('lbl_EMPBE_Product_Online') : $t('lbl_EMPBE_Product_Offline') }}
                    </span>
                  </div>
                  <div class="ad-list-chrome-cell ad-list-chrome-cell--status">
                    <span :class="statusTagClass(resolveAdStatusKey(row, fromAdmin))">
                      {{ $t(resolveAdStatusI18nKey(resolveAdStatusKey(row, fromAdmin), fromAdmin)) }}
                    </span>
                  </div>
                  <div class="ad-list-chrome-cell ad-list-chrome-cell--date">
                    {{ row.effectivePeriodText }}
                  </div>
                  <div class="ad-list-chrome-cell ad-list-chrome-cell--date">
                    {{ row.lastSubmitText }}
                  </div>
                </div>
              </div>
              <Pager
                class="ad-list-pager"
                :t_label="adTLabel('List', 'pager')"
                :skip="skip"
                :take="take"
                :total="totalCount"
                :button-count="gridPageable.buttonCount"
                :info="gridPageable.info"
                :type="gridPageable.type"
                :page-sizes="gridPageable.pageSizes"
                :previous-next="gridPageable.previousNext"
                @pagechange="handlePageChange"
                @pagesizechange="handlePageSizeChange"
              />
            </div>
          </intl>
        </localization>
      </div>
    </b-overlay>
    <AdListContactUs
      v-if="!fromAdmin"
      ref="contactUsDrawer"
    />
  </div>
</template>

<script>
import { Pager } from "@progress/kendo-vue-data-tools";
import { Input } from "@progress/kendo-vue-inputs";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { IntlProvider, LocalizationProvider } from "@progress/kendo-vue-intl";
import { adService, DEFAULT_LOGO_AD_THUMB_URL, hasAdListDraftIndicator } from "../../_services";
import {
  AD_ROW_STATUS,
  AD_ROW_STATUS_I18N,
  AD_ADMIN_ROW_STATUS_I18N,
  resolveAdStatusKey,
  resolveAdStatusI18nKey,
  DISPLAY_IN_I18N,
  PAGE_TYPE_I18N,
  PAGE_TYPE_ORDER,
  getAdPlatformLogoUrl,
  buildAdInfoCenterTLabel,
  resolveAdInfoCenterTrackingRoleId,
} from "./adDisplayConfig";
import { clearAdDetailNavContext, setAdDetailNavContext } from "./adDetailNavContext";
import MoreAdvertisingOptions from "./MoreAdvertisingOptions.vue";
import AdListContactUs from "./AdListContactUs.vue";

/** 列表七列：平台 / 类型 / 广告项 / 上架 / 审批状态 / 有效期 / 提交时间
 *  Ad Item 加宽给 Draft；有效期 min 需能完整显示 "YYYY-MM-DD to YYYY-MM-DD"
 */
const AD_LIST_COL_WIDTHS = [
  "minmax(140px, 12%)",
  "minmax(100px, 10%)",
  "minmax(280px, 30%)",
  "minmax(120px, 11%)",
  "minmax(120px, 10%)",
  "minmax(220px, 14%)",
  "minmax(130px, 13%)",
];

export default {
  name: "AdListView",
  components: {
    Pager,
    localization: LocalizationProvider,
    intl: IntlProvider,
    "k-input": Input,
    DropDownList,
    MoreAdvertisingOptions,
    AdListContactUs,
  },
  props: {
    companyId: { type: [String, Number], default: null },
    fromAdmin: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      AD_ROW_STATUS_I18N,
      loading: true,
      items: [],
      totalCount: 0,
      toBeSubmittedCount: 0,
      rejectedCount: 0,
      skip: 0,
      take: 10,
      showExploreMoreAdsPanel: false,
      keywordFilter: "",
      serverQuery: {
        displaySection: "",
        displayStatus: "",
        launchStatus: "",
        pageType: "",
        keyword: "",
        sortField: "",
        sortDir: "",
      },
      gridPageable: {
        buttonCount: 5,
        info: true,
        type: "numeric",
        pageSizes: [10, 25, 50],
        previousNext: true,
      },
      platformFilterDefault: null,
      pageTypeFilterDefault: null,
      statusFilterDefault: null,
      launchStatusFilterDefault: null,
      effectiveDateSortDefault: null,
      lastSubmitDateSortDefault: null,
      cdnURL: "https://cdn.adsalecdn.com/EMP/Backend/",
    };
  },
  computed: {
    resolvedCompanyId() {
      const id = this.companyId ?? this.$store.state.authentication.user.empCompanyId;
      return Number(id) || 0;
    },
    trackingRoleId() {
      return resolveAdInfoCenterTrackingRoleId(this.$store);
    },
    langid() {
      return localStorage.getItem("currentLangId");
    },
    kendoLang() {
      switch (this.langid) {
        case "1252":
          return "en-US";
        case "950":
          return "zh-Hant";
        case "936":
          return "zh-Hans";
        default:
          return "zh-Hans";
      }
    },
    /** 与产品资料一致：无图时用 ProductInfo/NoImage.svg */
    defaultAdThumbUrl() {
      return `${this.cdnURL}svg/ProductInfo/NoImage.svg`;
    },
    columns() {
      return [
        {
          field: "displaySection",
          title: this.$t("lbl_EMPBE_AdList_DigitalPlatform"),
          width: AD_LIST_COL_WIDTHS[0],
          cell: "platformTemplate",
        },
        {
          field: "pageType",
          title: this.$t("lbl_EMPBE_AdType"),
          width: AD_LIST_COL_WIDTHS[1],
          cell: "pageTypeTemplate",
        },
        {
          field: "adItemLabel",
          title: this.$t("lbl_EMPBE_AdList_AdItem"),
          width: AD_LIST_COL_WIDTHS[2],
          cell: "adItemTemplate",
        },
        {
          field: "isLaunch",
          title: this.$t("lbl_EMPBE_ProductsStatus"),
          width: AD_LIST_COL_WIDTHS[3],
          cell: "launchStatusTemplate",
        },
        {
          field: "displayStatus",
          title: this.$t("lbl_EMPBE_AdStatus"),
          width: AD_LIST_COL_WIDTHS[4],
          cell: "statusTemplate",
        },
        {
          field: "effectiveDateFrom",
          title: this.$t("lbl_EMPBE_Ad_Effective_Period"),
          width: AD_LIST_COL_WIDTHS[5],
          cell: "effectivePeriodTemplate",
        },
        {
          field: "lastSubmitDate",
          title: this.$t("lbl_EMPBE_AdList_LastSubmitted"),
          width: AD_LIST_COL_WIDTHS[6],
          cell: "lastSubmitTemplate",
        },
      ];
    },
    adListGridColumns() {
      return AD_LIST_COL_WIDTHS.join(" ");
    },
    statusFilterOptions() {
      const base = [{ value: "", text: this.$t("lbl_EMPBE_AdList_All") }];
      if (this.fromAdmin) {
        const adminOrder = [
          AD_ROW_STATUS.toBeSubmitted,
          AD_ROW_STATUS.pending,
          AD_ROW_STATUS.pendingManualAudit,
          AD_ROW_STATUS.pendingGoLive,
          AD_ROW_STATUS.live,
          AD_ROW_STATUS.rejected,
          AD_ROW_STATUS.expired,
        ];
        return base.concat(
          adminOrder.map((key) => ({
            value: key,
            text: this.$t(resolveAdStatusI18nKey(key, true)),
          }))
        );
      }
      const supplierOrder = [
        AD_ROW_STATUS.toBeSubmitted,
        AD_ROW_STATUS.pending,
        AD_ROW_STATUS.pendingManualAudit,
        AD_ROW_STATUS.pendingGoLive,
        AD_ROW_STATUS.live,
        AD_ROW_STATUS.rejected,
        AD_ROW_STATUS.expired,
      ];
      return base.concat(
        supplierOrder.map((key) => ({
          value: key,
          text: this.$t(AD_ROW_STATUS_I18N[key] || key),
        }))
      );
    },
    launchStatusFilterOptions() {
      return [
        { value: "", text: this.$t("lbl_EMPBE_AdList_All") },
        { value: "online", text: this.$t("lbl_EMPBE_Product_Online") },
        { value: "offline", text: this.$t("lbl_EMPBE_Product_Offline") },
      ];
    },
    pageTypeFilterOptions() {
      return [
        { value: "", text: this.$t("lbl_EMPBE_AdList_All") },
        ...PAGE_TYPE_ORDER.map((type) => ({
          value: type,
          text: this.$t(PAGE_TYPE_I18N[type]),
        })),
      ];
    },
    displayInFilterOptions() {
      return [
        { value: "", text: this.$t("lbl_EMPBE_AdList_All") },
        { value: "emp", text: this.$t(DISPLAY_IN_I18N.emp) },
        { value: "cps", text: this.$t(DISPLAY_IN_I18N.cps) },
      ];
    },
    dateSortingData() {
      return [
        { value: "", text: this.$t("Lbl_News_SortByDate") },
        { value: "asc", text: this.$t("lbl_EMP2_Sort_Asc") },
        { value: "desc", text: this.$t("lbl_EMP2_Sort_Dsc") },
      ];
    },
    platformFilterValue() {
      return this.platformFilterDefault || this.displayInFilterOptions[0];
    },
    pageTypeFilterValue() {
      return this.pageTypeFilterDefault || this.pageTypeFilterOptions[0];
    },
    statusFilterValue() {
      return this.statusFilterDefault || this.statusFilterOptions[0];
    },
    launchStatusFilterValue() {
      return this.launchStatusFilterDefault || this.launchStatusFilterOptions[0];
    },
    effectiveDateSortValue() {
      return this.effectiveDateSortDefault || this.dateSortingData[0];
    },
    lastSubmitDateSortValue() {
      return this.lastSubmitDateSortDefault || this.dateSortingData[0];
    },
    hasActiveFilter() {
      const q = this.serverQuery;
      return !!(
        q.displaySection
        || q.displayStatus
        || q.launchStatus
        || q.pageType
        || (q.keyword && String(q.keyword).trim())
        || q.sortField
      );
    },
    showEmptyPlaceholder() {
      return !this.loading && this.items.length === 0 && !this.hasActiveFilter;
    },
    empFrontendLangSlug() {
      const langMap = {
        "1252": "eng",
        "936": "simp",
        "950": "trad",
      };
      return langMap[String(this.langid || "")] || "eng";
    },
    learnMoreUrl() {
      return `https://www.chinaplasonline.com/eMarketplace/Supplier/${this.empFrontendLangSlug}#supplier_MembershipsBenefits`;
    },
  },
  watch: {
    resolvedCompanyId() {
      this.loadRows();
    },
    "$route.query.tab"(tab) {
      if (this.embedded) return;
      this.applyPageTypeRouteFilter(tab || "");
    },
  },
  methods: {
    resolveAdStatusKey,
    resolveAdStatusI18nKey,
    hasRowDraftIndicator(row) {
      return hasAdListDraftIndicator(row);
    },
    adListThumbSrc(row) {
      const thumb = row?.thumbnail && String(row.thumbnail).trim();
      if (thumb) return thumb;
      if (row?.pageType === "logoAd") return DEFAULT_LOGO_AD_THUMB_URL;
      return this.defaultAdThumbUrl;
    },
    onAdListThumbError(e, row) {
      const img = e?.target;
      if (!img || img.dataset.fallbackApplied === "1") return;
      img.dataset.fallbackApplied = "1";
      img.src = row?.pageType === "logoAd"
        ? DEFAULT_LOGO_AD_THUMB_URL
        : this.defaultAdThumbUrl;
    },
    toggleExploreMoreAdsPanel() {
      this.showExploreMoreAdsPanel = !this.showExploreMoreAdsPanel;
    },
    closeExploreMoreAdsPanel() {
      this.showExploreMoreAdsPanel = false;
    },
    openContactSales() {
      this.closeExploreMoreAdsPanel();
      this.$refs.contactUsDrawer?.open();
    },
    onDocumentClickExploreMore(e) {
      if (!this.showExploreMoreAdsPanel) return;
      const wrap = this.$refs.exploreMoreAdsWrap;
      if (wrap && !wrap.contains(e.target)) {
        this.closeExploreMoreAdsPanel();
      }
    },
    adTLabel(location, action, subType = '', adDetailId = null) {
      const actionKey = subType ? `${subType}_${action}` : action;
      return buildAdInfoCenterTLabel(
        location,
        actionKey,
        this.resolvedCompanyId,
        this.trackingRoleId,
        adDetailId
      );
    },
    refresh() {
      return this.loadRows();
    },
    async loadRows() {
      if (!this.resolvedCompanyId) {
        this.items = [];
        this.totalCount = 0;
        return;
      }
      this.loading = true;
      try {
        const pageIndex = Math.floor(this.skip / this.take) + 1;
        const keyword = (this.serverQuery.keyword || "").trim();
        const useClientKeywordFilter = !!keyword;
        const requestPageIndex = useClientKeywordFilter ? 1 : pageIndex;
        const requestPageSize = useClientKeywordFilter ? 2000 : this.take;
        const res = await adService.GetAdRecordList(
          this.resolvedCompanyId,
          this.langid,
          this.fromAdmin,
          {
            pageIndex: requestPageIndex,
            pageSize: requestPageSize,
            ...this.serverQuery,
            // Keyword is handled locally so ad item text can be matched by translated label.
            keyword: useClientKeywordFilter ? "" : this.serverQuery.keyword,
          }
        );
        if (res.isSuccessful && res.data) {
          const payload = res.data;
          let rows = (payload.items || []).map((row) => this.enrichRow(row));
          if (useClientKeywordFilter) {
            rows = rows.filter((row) => this.matchesKeyword(row, keyword));
            this.totalCount = rows.length;
            this.items = rows.slice(this.skip, this.skip + this.take);
          } else {
            this.items = rows;
            this.totalCount = payload.totalCount || this.items.length || 0;
          }
          this.toBeSubmittedCount = payload.toBeSubmittedCount || 0;
          this.rejectedCount = payload.rejectedCount || 0;
        } else {
          this.items = [];
          this.totalCount = 0;
          this.toBeSubmittedCount = 0;
          this.rejectedCount = 0;
          if (res && !res.isSuccessful) {
            console.error('GetAdRecordList failed', res.systemErrorMsg || res);
          }
        }
        this.$emit("list-loaded", { toBeSubmittedCount: this.toBeSubmittedCount });
        if (!this.fromAdmin) {
          this.$store.dispatch("unread/setAdBadgeCounts", {
            toBeSubmittedCount: this.toBeSubmittedCount,
            rejectedCount: this.rejectedCount,
          });
        }
      } finally {
        this.loading = false;
      }
    },
    enrichRow(row) {
      const displaySection = row.displayInKey === DISPLAY_IN_I18N.cps ? "cps" : "emp";
      const pcId = row.pcAdDetailId ?? null;
      const mobId = row.mobileAdDetailId ?? null;
      const showPairedDetailIds = !!(pcId && mobId && Number(pcId) !== Number(mobId));
      return {
        ...row,
        displaySection,
        displayInLabel: this.$t(row.displayInKey),
        pageTypeLabel: row.pageType && PAGE_TYPE_I18N[row.pageType]
          ? this.$t(PAGE_TYPE_I18N[row.pageType])
          : (row.pageType || "—"),
        adItemLabel: this.$t(row.titleKey),
        adDetailIdText: String(row.adDetailId ?? ""),
        showPairedDetailIds,
        effectivePeriodText: this.formatEffectivePeriod(row),
        lastSubmitText: this.formatDateTime(row.lastSubmitDate),
      };
    },
    handlePageChange(event) {
      this.skip = event.skip;
      this.take = event.take;
      this.loadRows();
    },
    handlePageSizeChange(event) {
      this.skip = event.skip;
      this.take = event.take;
      this.loadRows();
    },
    onDataRowClick(row, event) {
      if (event?.target?.closest?.(".ad-list-edit-btn")) return;
      this.goToDetail(row);
    },
    applySelectFilter(field, value) {
      if (field === "displaySection") {
        this.serverQuery.displaySection = value || "";
        this.platformFilterDefault = this.displayInFilterOptions.find((o) => o.value === value)
          || this.displayInFilterOptions[0];
      }
      if (field === "displayStatus") {
        this.serverQuery.displayStatus = value || "";
        this.statusFilterDefault = this.statusFilterOptions.find((o) => o.value === value)
          || this.statusFilterOptions[0];
      }
      if (field === "launchStatus") {
        this.serverQuery.launchStatus = value || "";
        this.launchStatusFilterDefault = this.launchStatusFilterOptions.find((o) => o.value === value)
          || this.launchStatusFilterOptions[0];
      }
      if (field === "pageType") {
        this.serverQuery.pageType = value || "";
        this.pageTypeFilterDefault = this.pageTypeFilterOptions.find((o) => o.value === value)
          || this.pageTypeFilterOptions[0];
      }
      this.skip = 0;
      this.loadRows();
    },
    applyKeywordFilter(ev) {
      const value = ev?.value != null ? ev.value : (ev?.target?.value ?? "");
      this.keywordFilter = value || "";
      this.serverQuery.keyword = this.keywordFilter.trim();
      this.skip = 0;
      this.loadRows();
    },
    applyDateSort(field, dir) {
      this.serverQuery.sortField = dir ? field : "";
      this.serverQuery.sortDir = dir || "";
      const selected = this.dateSortingData.find((o) => o.value === dir)
        || this.dateSortingData[0];
      if (field === "effectiveDateFrom") {
        this.effectiveDateSortDefault = selected;
      } else if (field === "lastSubmitDate") {
        this.lastSubmitDateSortDefault = selected;
      }
      this.skip = 0;
      this.loadRows();
    },
    applyPageTypeRouteFilter(pageType) {
      this.serverQuery.pageType = pageType || "";
      this.pageTypeFilterDefault = this.pageTypeFilterOptions.find((o) => o.value === pageType)
        || this.pageTypeFilterOptions[0];
      this.skip = 0;
      if (this.resolvedCompanyId) this.loadRows();
    },
    platformLogoUrl(section) {
      return getAdPlatformLogoUrl(section);
    },
    onPlatformLogoError(event) {
      if (event?.target) event.target.style.visibility = "hidden";
    },
    isRejectedRow(item) {
      return item?.displayStatus === AD_ROW_STATUS.rejected;
    },
    isRowLaunchOnline(row) {
      const raw = row?.isLaunch;
      return raw === 1 || raw === true || raw === '1';
    },
    statusTagClass(status) {
      const map = {
        live: "ad-status-tag ad-status-tag--live",
        pendingGoLive: "ad-status-tag ad-status-tag--pending-go-live",
        pending: "ad-status-tag ad-status-tag--pending",
        aiReviewing: "ad-status-tag ad-status-tag--ai-reviewing",
        pendingManualAudit: "ad-status-tag ad-status-tag--pending-manual",
        rejected: "ad-status-tag ad-status-tag--rejected",
        toBeSubmitted: "ad-status-tag ad-status-tag--draft",
        expired: "ad-status-tag ad-status-tag--expired",
      };
      return map[status] || "ad-status-tag ad-status-tag--draft";
    },
    goToDetail(row) {
      const payload = {
        row,
        adDetailId: row.adDetailId,
        adPackageId: row.adPackageId ?? null,
        pageType: row.pageType,
        pcAdDetailId: row.pcAdDetailId ?? null,
        pcAdPackageId: row.pcAdPackageId ?? null,
        mobileAdDetailId: row.mobileAdDetailId ?? null,
        mobileAdPackageId: row.mobileAdPackageId ?? null,
      };
      const indexInPage = this.items.findIndex((r) =>
        String(r.adDetailId) === String(row.adDetailId)
        || (row.pcAdDetailId && String(r.pcAdDetailId) === String(row.pcAdDetailId))
        || (row.mobileAdDetailId && String(r.mobileAdDetailId) === String(row.mobileAdDetailId))
      );
      const pageIndex = Math.floor(this.skip / this.take) + 1;
      const safeIndex = indexInPage >= 0 ? indexInPage : 0;
      setAdDetailNavContext(this.resolvedCompanyId, {
        serverQuery: { ...this.serverQuery },
        pageIndex,
        pageSize: this.take,
        indexInPage: safeIndex,
        globalIndex: this.skip + safeIndex,
        totalCount: this.totalCount,
        items: this.items.map((r) => adService.toAdDetailNavRow(r)),
      });
      if (this.embedded) {
        this.$emit("open-detail", payload);
        return;
      }
      this.$router.push(adService.buildAdDetailRouteLocation(row));
    },
    formatDateTime(raw) {
      if (!raw || raw === "-") return "—";
      return String(raw).replace(/:\d{2}$/, "");
    },
    formatDateOnly(raw) {
      if (!raw || raw === "-") return null;
      return String(raw).split(" ")[0];
    },
    formatEffectivePeriod(row) {
      const from = this.formatDateOnly(row.effectiveDateFrom);
      const to = this.formatDateOnly(row.effectiveDateTo);
      if (!from && !to) return "—";
      return `${from || "—"} to ${to || "—"}`;
    },
    matchesKeyword(row, keyword) {
      const q = String(keyword || "").trim().toLowerCase();
      if (!q) return true;
      const fields = [
        row.adItemLabel,
        row.adDetailId,
        row.pcAdDetailId,
        row.mobileAdDetailId,
        row.subType,
      ];
      return fields.some((value) => String(value ?? "").toLowerCase().includes(q));
    },
  },
  mounted() {
    clearAdDetailNavContext();
    this.platformFilterDefault = this.displayInFilterOptions[0];
    this.pageTypeFilterDefault = this.pageTypeFilterOptions[0];
    this.statusFilterDefault = this.statusFilterOptions[0];
    this.effectiveDateSortDefault = this.dateSortingData[0];
    this.lastSubmitDateSortDefault = this.dateSortingData[0];
    if (!this.embedded) {
      this.applyPageTypeRouteFilter(this.$route.query.tab || "");
    } else {
      this.loadRows();
    }
    this.$emit("adTabChange", "");
    document.addEventListener("click", this.onDocumentClickExploreMore, true);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClickExploreMore, true);
  },
};
</script>

<style lang="scss" scoped>
.ad-list-card {
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ad-list-page-heading {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 37px;
  overflow: visible;
  z-index: 5;
}

.ad-list-page-heading__main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  min-width: 0;
}

.ad-list-page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.8);
  letter-spacing: -0.264px;
}

.ad-list-pending-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffd339;
  color: rgba(0, 0, 0, 0.8);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.154px;
  white-space: nowrap;
}

.ad-list-explore-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #7968ff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: -0.154px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(115, 102, 255, 0.06);
    color: #6356e8;
  }

  &:active {
    background: rgba(115, 102, 255, 0.1);
    color: #5749d6;
  }
}

.ad-list-explore-wrap {
  position: relative;
  flex-shrink: 0;
  z-index: 20;
}

.ad-list-explore-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  width: 480px;
  max-width: min(480px, calc(100vw - 48px));
}

.ad-list-explore-btn__icon {
  width: 20px;
  height: 20px;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  flex-shrink: 0;
}

.ad-list-explore-btn__text {
  padding: 0 4px;
}

.ad-list-reject-banner {
  padding: 16px;
  background: #ffe6e6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ad-list-reject-banner__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ad-list-reject-banner__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.ad-list-reject-banner__title {
  color: #cd1515;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.ad-list-reject-banner__desc {
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
  font-size: 14px;
  line-height: 1.3;
}

.ad-list-platform-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  min-width: 0;
  width: 100%;
}

.ad-list-platform-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #f9f9f9;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ad-list-platform-name {
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
  min-width: 0;
  flex: 1;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ad-list-page-type {
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
  display: block;
  min-width: 0;
  width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ad-list-item-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
}

.ad-list-thumb-wrap {
  flex-shrink: 0;
}

.ad-list-thumb {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #f9f9f9;
  display: block;
}

.ad-list-draft-pill {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #ffd02c;
  color: rgba(0, 0, 0, 0.8);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.154px;
  white-space: nowrap;
}

.ad-list-draft-pill__icon {
  flex-shrink: 0;
  display: block;
  width: 14px;
  height: 16px;
}

.ad-list-item-text {
  flex: 1;
  min-width: 0;
}

.ad-list-item-title {
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
  font-weight: 400;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ad-list-item-title--rejected {
  color: #cd1515;
}

.ad-list-item-id {
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.ad-list-item-id-sep {
  color: rgba(0, 0, 0, 0.35);
}

.ad-list-edit-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border: 1px solid #f2f3f5;
  border-radius: 4px;
  background: #fff;
  color: #7366ff;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(115, 102, 255, 0.08);
    border-color: rgba(115, 102, 255, 0.35);
    color: #6356e8;
  }

  &:active {
    background: rgba(115, 102, 255, 0.14);
  }
}

.ad-list-edit-icon {
  font-size: 12px;
}


.ad-list-table-shell {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ad-list-chrome-row,
.ad-list-data-row {
  width: 100%;
}

.ad-list-chrome {
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: none;
  min-width: 1180px;
}

.ad-list-chrome-row {
  display: grid;
  width: 100%;
}

.ad-list-chrome-cell {
  min-width: 0;
  padding: 8px;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  box-sizing: border-box;

  &:last-child {
    border-right: none;
  }
}

.ad-list-chrome-row--head .ad-list-chrome-cell {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.8);
  letter-spacing: 0.15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ad-list-chrome-row--filter {
  ::v-deep .k-input,
  ::v-deep .k-dropdownlist,
  ::v-deep .k-picker {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  ::v-deep .k-input-inner,
  ::v-deep .k-dropdown-wrap,
  ::v-deep .k-picker-wrap {
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    min-height: 32px;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.5);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  ::v-deep .k-input-inner:hover,
  ::v-deep .k-picker:hover .k-input-inner {
    border-color: rgba(115, 102, 255, 0.45);
  }

  ::v-deep .k-input-inner:focus,
  ::v-deep .k-picker:focus-within .k-input-inner {
    border-color: #7366ff;
    box-shadow: 0 0 0 2px rgba(115, 102, 255, 0.12);
  }
}

.ad-list-body {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  min-width: 1180px;
}

.ad-list-data-row {
  display: grid;
  width: 100%;
  cursor: pointer;
  background: #fff;
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
  transition: background-color 0.15s ease;

  .ad-list-chrome-cell {
    display: flex;
    align-items: center;
    min-height: 100%;
    min-width: 0;
    overflow: hidden;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .ad-list-chrome-cell--launch {
    overflow: hidden;
  }

  .ad-list-chrome-cell--status {
    overflow: hidden;

    .ad-status-tag {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .ad-list-chrome-cell--date {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: normal;
    word-break: normal;
  }

  &:hover:not(.ad-list-data-row--rejected) {
    background: rgba(115, 102, 255, 0.04);
  }

  &--rejected {
    background: #fcebed;

    &:hover {
      background: #f6e0e3;
    }
  }

  &:last-child .ad-list-chrome-cell {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }
}

.ad-list-launch-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
  height: 26px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  background: rgba(0, 255, 0, 0.25);
  color: #51bb25;
}

.ad-list-launch-badge.is-offline {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.05);
}

.ad-list-launch-badge__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9e9e9e;
  flex-shrink: 0;
}

.ad-list-launch-badge.is-online .ad-list-launch-badge__dot {
  background: #51bb25;
}

::v-deep .ad-list-pager {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-top: none;
  background: #fff;
  min-width: 1180px;

  .k-link {
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .k-link:hover:not(.k-disabled):not(.k-state-disabled):not(.k-selected) {
    background: rgba(115, 102, 255, 0.08);
    color: #7366ff;
  }

  .k-pager-numbers .k-link.k-selected {
    background: #7366ff;
    color: #fff;
  }

  .k-pager-numbers .k-link.k-selected:hover {
    background: #6356e8;
    color: #fff;
  }
}
</style>
